import React, { useState, useEffect, useCallback, useContext } from 'react';

import './App.css';
import * as mat from './lib/jsmatrix';
import { MatF32, VecF32 } from './lib/jsmatrix';
import {
  CheckBox, ValueSlider, NumberInput
} from './inputComponents.js';

import {
  computeFrustumPlanes,
  intersectLinePlane,
  planeFromPointNormal,
  transformPlane,
  pointInside,
  addz
} from './pointUtils.js'

import { HelpWindow } from "./HelpWindow.js";

import { useEventHandler } from './components.js';
import {
  Data,
  formatNumber
} from './utils.js';
import {
  RGBA
} from './color.js';
import { CanvasOutput, SVGOutput } from './renderoutput.js';
import { Screen } from './utils.js'
import { useFirstPersonController } from './camera';
import {
  Displayable,
  Polygon,
  LineStrip,
  Point,
  Text
} from './geom.js'

import {
  zoom_scale,
  computeViewportHalfSize
} from './commonView.js';

import {
  CreatePointTool,
  CreateRectTool,
  SelectEditTool,
  ViewEditTool,
  LoadSaveTool,
  FreeHandPolyTool,
  CameraEditTool,
  useViewportMove
} from './tools.js';
import { ACTION_MOUSE_CLICK_LEFT, ACTION_MOUSE_UP_LEFT, ACTION_MOUSE_UP_RIGHT, ACTION_MOUSE_DOWN_LEFT, ACTION_MOUSE_DOWN_RIGHT, ACTION_MOUSE_MOVE } from './actions';

const MAX_ZOOM = 50;

function updateViewportAspect(current, zoom) {
  const { center: c, halfSize: h } = current;
  const hw = h.at(0);
  const hh = h.at(1);
  const aspect = hw / hh;
  // old max size
  const size = computeViewportHalfSize(zoom);
  if (hw > hh) {
    const newHalfSize = VecF32.from([size, size / aspect, 0]);
    return {
      center: c,
      halfSize: newHalfSize,
      zoomLevel: zoom
    }
  }
  else {
    const newHalfSize = VecF32.from([size * aspect, size, 0]);

    return {
      center: c,
      halfSize: newHalfSize,
      zoomLevel: zoom
    }
  }
}

function clipDrawable(d, clipplanes) {

  if (clipplanes) {

    for (let i = 0; i < clipplanes.length; i++) {
      const plane = clipplanes[i];
      d = d.clip(plane);
      if (!d) {
        return null;
      }
    }
  }

  return d;

}


function useComponentRef() {
  const [element, setElement] = useState(null)
  const ref = useCallback(e => {
    setElement(e);
  }, []);

  return [element, ref];
}





function event2Coordinate(e, flipy) {
  flipy = mat.optional(flipy, true);
  const rect = e.target.getBoundingClientRect();
  const x = e.clientX - rect.left; //x position within the element.
  let y = e.clientY - rect.top;  //y position within the element.
  if (flipy) {
    y = rect.height - 1 - y;
  }
  return VecF32.from([x, y]);
}


function transformMouse(mp, M) {
  const mp4 = VecF32.from([mp.at(0), mp.at(1), 0, 1]);
  const pWorld = VecF32.copy(mat.subvec(mat.mult(M, mp4), 0, 2));
  return pWorld;
}


function ToolSelect(props) {

  const onToolChange = props.onToolChange;
  const currentTool = props.tool;
  return (
    <>
      <span className="title">
        Tools
      </span>
      <div className="toolselect-buttons">
        <button disabled={currentTool === TOOL_CREATE_RECT} onClick={() => { onToolChange(TOOL_CREATE_RECT); }}>Rectangle</button>
        <button disabled={currentTool === TOOL_CREATE} onClick={() => { onToolChange(TOOL_CREATE); }}>Points</button>
        <button disabled={currentTool === TOOL_FREEDRAW} onClick={() => { onToolChange(TOOL_FREEDRAW); }}>Freehand</button>
        <button disabled={currentTool === TOOL_SELECT} onClick={() => { onToolChange(TOOL_SELECT); }}>Select</button>
        <button disabled={currentTool === TOOL_VIEW} onClick={() => { onToolChange(TOOL_VIEW); }}>Move viewport</button>
        <button disabled={currentTool === TOOL_CAMERA} onClick={() => { onToolChange(TOOL_CAMERA); }}>Camera</button>
        <button disabled={currentTool === TOOL_LOAD_SAVE} onClick={() => { onToolChange(TOOL_LOAD_SAVE); }}>Load/Save</button>
      </div>
    </>
  );
}

const TOOL_CREATE = 0;
const TOOL_SELECT = 1;
const TOOL_VIEW = 2;
const TOOL_FREEDRAW = 3;
const TOOL_LOAD_SAVE = 4;
const TOOL_CREATE_RECT = 5;
const TOOL_CAMERA = 6;




const STATE_CANVAS_DRAG_WAITING = 0;
const STATE_CANVAS_DRAG_DRAGGING = 1;
function useCanvasDrag({ canvas, context, viewTransforms }) {

  const [dragState, setDragState] = useState(STATE_CANVAS_DRAG_WAITING);

  const [lastMousePos, setLastMousePos] = useState(VecF32.from([0, 0]));

  const handleMouseMove = useCallback((e) => {
    if (dragState !== STATE_CANVAS_DRAG_DRAGGING) {
      return;
    }
    const currentPos = event2Coordinate(e);
    setLastMousePos(() => currentPos);
    context.setViewport(current => {
      const currentCenter = current.center;
      const deltaCanvas = mat.fromTo(currentPos, lastMousePos);
      const deltaWorld = mat.subvec(mat.mult(viewTransforms.MCanvas2Viewport,
        mat.hvec(addz(deltaCanvas, 0), 0))
        , 0, 3
      );
      return {
        center: mat.add(currentCenter, deltaWorld),
        halfSize: current.halfSize,
        zoomLevel: current.zoomLevel
      };
    });
  }, [context, dragState, lastMousePos, viewTransforms]);
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 1) {
      return;
    }
    setLastMousePos(() => event2Coordinate(e));
    setDragState(() => STATE_CANVAS_DRAG_DRAGGING);
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const handleMouseUp = useCallback((e) => {
    if (e.button !== 1) {
      return;
    }

    setDragState(() => STATE_CANVAS_DRAG_WAITING);
    e.preventDefault();
    e.stopPropagation();
  }, []);

  useEventHandler({
    element: canvas,
    eventName: "mousedown",
    handleEvent: handleMouseDown
  });

  useEventHandler({
    element: canvas,
    eventName: "mouseup",
    handleEvent: handleMouseUp
  });

  useEventHandler({
    element: canvas,
    eventName: "mousemove",
    handleEvent: handleMouseMove
  });

  useEventHandler({
    element: canvas,
    eventName: "mouseleave",
    handleEvent: () => {
      setDragState(() => STATE_CANVAS_DRAG_WAITING)
    }
  });

  useEffect(() => {
    setDragState(() => STATE_CANVAS_DRAG_WAITING);
  }, [canvas]);

}

function EditCanvas(props) {

  const [canvas, canvasRef] = useComponentRef();
  const context = useContext(ModelContext);

  const [tool, setTool] = useState(TOOL_CREATE_RECT);
  const [mouse, setMouse] = useState({
    mousePosCanvas: VecF32.from([Infinity, Infinity]),
    mousePosWorld: VecF32.from([Infinity, Infinity]),
    snappedMousePosCanvas: VecF32.from([Infinity, Infinity]),
    snappedMousePosWorld: VecF32.from([Infinity, Infinity]),
    inside: false
  });

  const [overlayObjects, setOverlayObjects] = useState([]);

  const [currentTool, setCurrentTool] = useState(CreatePointTool.new());

  const [snapToGrid, setSnapToGrid] = useState(true);
  const [viewTransforms, setViewTransforms] = useState({
    MNormalized2Canvas: MatF32.id(4, 4),
    MViewport2Normalized: MatF32.id(4, 4),
    MViewPort2Canvas: MatF32.id(4, 4),
    MCanvas2Viewport: MatF32.id(4, 4),
    canvasSize:
    {
      width: 0,
      height: 0
    }
  })

  const [toolVisNeedsUpdate, setToolVisNeedsUpdate] = useState(0);

  const requestVisUpdate = () => setToolVisNeedsUpdate(v => v + 1);

  const [toolState, setToolState] = useState(

    {
      mouse: mouse,
      overlay: {
        overlayObjects: overlayObjects,
        setOverlayObjects: setOverlayObjects
      },
      viewTransforms: viewTransforms,
      context: context,
      requestVisUpdate: requestVisUpdate
    }
  );

  useCanvasDrag({
    canvas, context, viewTransforms
  });

  useViewportMove({
    viewTransforms, setViewport: context.setViewport,
    mouse,
    speed: 10.0
  });

  useEffect(() => {
    setToolState(() => {
      return {
        mouse: mouse,
        overlay: {
          setOverlayObjects: setOverlayObjects
        },
        viewTransforms: viewTransforms,
        context: context,
        requestVisUpdate: requestVisUpdate
      }
    });
  }, [mouse, context, viewTransforms]);

  const resetTool = (tool) => {
    setOverlayObjects([]);
    if (tool === TOOL_CREATE) {
      setCurrentTool(() => CreatePointTool.new());
    } else if (tool === TOOL_SELECT) {
      setCurrentTool(() => SelectEditTool.new());
    } else if (tool === TOOL_VIEW) {
      setCurrentTool(ViewEditTool.new());
    } else if (tool === TOOL_FREEDRAW) {
      setCurrentTool(FreeHandPolyTool.new());
    } else if (tool === TOOL_LOAD_SAVE) {
      setCurrentTool(LoadSaveTool.new());
    } else if (tool === TOOL_CREATE_RECT) {
      setCurrentTool(CreateRectTool.new());
    } else if (tool === TOOL_CAMERA) {
      setCurrentTool(CameraEditTool.new());
    }
  };

  useEffect(() => {
    // reset specific tool
    // currently only create has states
    resetTool(tool);
  }, [tool]);

  const { viewport, setViewport } = context;
  // const { center: viewc, halfSize: viewh } = viewport;


  useEffect(() => {
    // init stuff
  }, []);




  useEffect(() => {
    if (!canvas) {
      return;
    }
    const { center, halfSize } = viewport;
    const transforms = {};
    const wh = canvas.width / 2;
    const hh = canvas.height / 2;
    transforms.MNormalized2Canvas = mat.mult(
      mat.translation(VecF32.from([wh, hh, 0])),
      mat.scaling(VecF32.from([wh, hh, 1])));

    const sx = halfSize.at(0) !== 0 ? 1 / halfSize.at(0) : 0;
    const sy = halfSize.at(1) !== 0 ? 1 / halfSize.at(1) : 0;

    transforms.MViewport2Normalized = mat.mult(mat.scaling(VecF32.from([sx, sy, 1])),
      mat.translation(mat.neg(center)));

    transforms.MViewPort2Canvas = mat.mult(transforms.MNormalized2Canvas, transforms.MViewport2Normalized);
    transforms.MCanvas2Viewport = mat.inv(transforms.MViewPort2Canvas);
    transforms.canvasSize = {
      width: canvas.width,
      height: canvas.height
    };
    setViewTransforms(transforms);

  }, [canvas, viewport]);

  const onResize = useCallback((width, height) => {
    const aspect = width / height;


    setViewport(viewport => {
      const { center: c, halfSize: h } = viewport;
      const hw = h.at(0);
      const hh = h.at(1);
      // old max size
      const size = Math.max(hw, hh);
      if (width > height) {
        const newHalfSize = VecF32.from([size, size / aspect, 0]);
        return {
          center: c,
          halfSize: newHalfSize,
          zoomLevel: viewport.zoomLevel
        }
      }
      else {
        const newHalfSize = VecF32.from([size * aspect, size, 0]);

        return {
          center: c,
          halfSize: newHalfSize,
          zoomLevel: viewport.zoomLevel
        }
      }
    });

  }, [setViewport]);

  const { objects } = props;
  // Rendering
  useEffect(() => {
    if (!canvas) {
      return;
    }
    const boundgridData = Data.new(
      {
        strokeStyle: RGBA.new(0.2, 0.2, 0.2, 0.6),
      }
    );

    const { center: viewc, halfSize: viewh } = viewport;

    const output = CanvasOutput.new(Screen.new().canvas(canvas));
    output.initFrame();


    const MView = viewTransforms.MViewPort2Canvas;
    const displayObjects = [];

    const floorGridData = Data.new({
      strokeStyle: RGBA.AQUA.cp().a(0.45)
    });

    const floorgrid = [];
    {
      const bmin = mat.sub(viewc, viewh);
      let bx = bmin.at(0);
      let by = bmin.at(1);
      const cellSize = zoom_scale(viewport.zoomLevel);
      bx = cellSize * Math.floor(bx / cellSize);
      by = cellSize * Math.floor(by / cellSize);

      bmin.set(bx, 0);
      bmin.set(by, 0);

      const dx = 2.0 * viewh.at(0);
      const dy = 2.0 * viewh.at(1);

      const numX = Math.ceil(dx / cellSize) + 1;
      const numY = Math.ceil(dy / cellSize) + 1;
      const vx = mat.scale(mat.X, cellSize * numX);
      const vy = mat.scale(mat.Y, cellSize * numY);

      for (let i = 0; i <= numX; i++) {
        const p0 = VecF32.from([bx + i * cellSize, by, 0]);
        const p1 = mat.add(p0, vy);
        floorgrid.push(LineStrip.new(MatF32.fromCols([p0, p1])));
      }

      for (let i = 0; i <= numY; i++) {
        const p0 = VecF32.from([bx, by + i * cellSize, 0]);
        const p1 = mat.add(p0, vx);
        floorgrid.push(LineStrip.new(MatF32.fromCols([p0, p1])));
      }
    }

    for (let j = 0; j < floorgrid.length; j++) {
      const lj = floorgrid[j];
      prepareAndAdd(displayObjects, lj, floorGridData, [], MView, -1);
    }

    if (objects) {
      // draw bounds
      for (let i = 0; i < objects.length; i++) {
        const dp = objects[i].xyboundsWorld;

        const boundData = Data.new({
          fillStyle: RGBA.new(0, 0, 0, 0),
          strokeStyle: RGBA.RED.cp().a(0.5)
        });
        prepareAndAdd(displayObjects, Polygon.new(dp), boundData, [], MView, objects[i].options.z);



      }

      for (let i = 0; i < objects.length; i++) {
        const oi = objects[i];

        const bb = oi.xyboundsWorld;

        if (oi.options.showBoundGridBottom || oi.options.showBoundGridTop) {
          // const lines = createSideGrid(mat.col(bb, 0), mat.col(bb, 1), mat.col(bb, 2), mat.col(bb, 3), null);
          // lines.forEach(l => {
          //   prepareAndAdd(displayObjects, l, boundgridData, [], MView, oi.options.z);
          // });

          const polySideGrids = createConstantLengthGrid({
            points: MatF32.fromCols([mat.col(bb, 0), mat.col(bb, 1)]),
            upDir: mat.normalize(mat.cross(mat.Z,
              mat.fromTo(mat.col(bb, 0), mat.col(bb, 1)))),
            upHeight: mat.norm(mat.sub(mat.col(bb, 3), mat.col(bb, 0))),
            cellLength: objects[i].options.gridCellLength,
            cullOptions: CULL_NONE
          });

          prepareAll({
            queue: displayObjects, drawables: polySideGrids,
            styleData: boundgridData, M: MView
          });
        }

      }


      for (let i = 0; i < objects.length; i++) {
        const oi = objects[i];
        prepareAndAdd(displayObjects, oi.drawableWorld, oi.styleData, [], MView, oi.options.z, oi.id);

      }
    }

    overlayObjects.forEach(o => {
      prepareAndAdd(displayObjects, o.drawableWorld, o.styleData, [], MView, Infinity, o.options.z, o.id);
    });

    displayObjects.sort((a, b) => a.z === b.z ? a.id - b.id : a.z - b.z);

    for (let i = 0; i < displayObjects.length; i++) {
      const di = displayObjects[i];
      di.drawable.draw(output, di.styleData);
    }

    output.finishFrame();

  }, [objects, overlayObjects, viewTransforms, viewport, canvas]);


  const handleAction = action => {

    currentTool.update(toolState, action);

  };
  useEffect(() => {
    currentTool.updateVis(toolState);
  }, [currentTool, toolState, toolVisNeedsUpdate]);

  const snapMousePos = pWorld => {
    if (snapToGrid) {
      const cellSize = zoom_scale(viewport.zoomLevel);

      let x = pWorld.at(0);
      let y = pWorld.at(1);

      x = cellSize * Math.floor(x / cellSize);
      y = cellSize * Math.floor(y / cellSize);
      return VecF32.from([x, y]);
    }
    else {
      return pWorld;
    }
  }

  const onClick = e => {
    if (e.button === 0) {
      handleAction({
        type: ACTION_MOUSE_CLICK_LEFT
      });
    }
    e.stopPropagation();
    e.preventDefault();

  };

  const onMouseDown = e => {

    let action;
    switch (e.button) {
      case 0: action = ACTION_MOUSE_DOWN_LEFT;
        break;
      case 2: action = ACTION_MOUSE_DOWN_RIGHT;
        break;
      default:
        return;
    }
    handleAction({
      type: action
    });
    e.stopPropagation();
    e.preventDefault();



  };

  const onMouseUp = e => {
    let action;
    switch (e.button) {
      case 0: action = ACTION_MOUSE_UP_LEFT;
        break;
      case 2: action = ACTION_MOUSE_UP_RIGHT;
        break;
      default:
        return;
    }
    handleAction({
      type: action
    });
    e.stopPropagation();
    e.preventDefault();


  };

  const onMouseMove = (e) => {

    const mp = event2Coordinate(e);
    const mpWorld = transformMouse(mp, viewTransforms.MCanvas2Viewport);
    const mpWorldSnapped = snapMousePos(mpWorld);
    const mpSnapped = transformMouse(mpWorldSnapped, viewTransforms.MViewPort2Canvas);

    const mouseNew = {
      mousePosCanvas: mp,
      snappedMousePosCanvas: mpSnapped,
      mousePosWorld: mpWorld,
      snappedMousePosWorld: mpWorldSnapped,
      inside: true
    };

    setMouse(() => mouseNew);

    handleAction({
      type: ACTION_MOUSE_MOVE,
      mouse: mouseNew
    });

  };

  const onMouseLeave = e => {
    setMouse(current => {
      const mouseNew = Object.assign({}, current);
      mouseNew.inside = false;
      return mouseNew;
    });
  };

  const onMouseEnter = e => {
    const mp = event2Coordinate(e);
    const mpWorld = transformMouse(mp, viewTransforms.MCanvas2Viewport);
    const mpWorldSnapped = snapMousePos(mpWorld);
    const mpSnapped = transformMouse(mpWorldSnapped, viewTransforms.MViewPort2Canvas);

    const mouseNew = {
      mousePosCanvas: mp,
      snappedMousePosCanvas: mpSnapped,
      mousePosWorld: mpWorld,
      snappedMousePosWorld: mpWorldSnapped,
      inside: true
    };

    setMouse(() => mouseNew);

  };

  const handleZoom = s => setViewport(cur => {

    const newzoom = s;

    return updateViewportAspect(cur, newzoom);
  });

  const handleZoomUpdate = s => setViewport(cur => {

    const newzoom = Math.min(Math.max(0, cur.zoomLevel + s), MAX_ZOOM);
    return updateViewportAspect(cur, newzoom);
  });

  return (<>
    <div className="edit-container">
      <div className="edit-entry">
        <canvas ref={canvasRef} width={props.width} height={props.height}
          onClick={onClick} onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave} onMouseEnter={onMouseEnter}
          onMouseDown={onMouseDown} onMouseUp={onMouseUp}
          onWheel={e => {
            handleZoomUpdate(e.deltaY >= 0 ? 1 : -1);
            e.preventDefault();
          }}
        >Canvas not supported</canvas>
      </div>
      <div className="edit-sidebar">
        <div className="edit-sidebar-entry">
          <span className="title">
            Options
          </span>
          <HelpWindow />
          <CanvasResizer canvas={canvas}
            buttonName="Resize edit canvas"
            handleSizeChange={
              (w, h) => {
                onResize(w, h);
              }
            }
          />
          <CheckBox checked={snapToGrid} labelText="Snap cursor to grid"
            handleChange={setSnapToGrid} />
        </div>
        <div className="edit-sidebar-entry">
          <ToolSelect tool={tool} onToolChange={tool => {
            setTool(tool);
          }}></ToolSelect>
        </div>
        <div className="edit-sidebar-entry">
          <span className="title">
            Viewport
          </span>
          <div>
            <div>
              Grid cell length: {formatNumber(zoom_scale(viewport.zoomLevel), 4)}
            </div>
            <div>
              <ValueSlider valueName="Zoom level"
                min={0} max={50} step={1} precision={0}
                value={viewport.zoomLevel}
                handleChange={handleZoom}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="edit-container">
      {currentTool.render(toolState)}
    </div>
  </>
  );
}


const CULL_NONE = 0;
const CULL_BACK = 1;
const CULL_FRONT = 2;
const CULL_FRONT_AND_BACK = 3;

function options2CullTypeSide(options) {
  if (options.showSideGridFront && options.showSideGridBack) {
    return CULL_NONE;
  }

  if (options.showSideGridFront) {
    return CULL_BACK;
  }

  if (options.showSideGridBack) {
    return CULL_FRONT;
  }

  return CULL_FRONT_AND_BACK;

}


function doCull(a, n, camPos, cullType) {
  if (!camPos) {
    return false;
  }
  if (cullType === CULL_NONE) {
    return false;
  }

  if (cullType === CULL_FRONT_AND_BACK) {
    return true;
  }
  const d = mat.dot(n, mat.fromTo(camPos, a));

  return cullType === CULL_BACK ? d > 0 : d < 0;

}

function prepareAll({ queue, drawables, styleData = Data.new(), clipplanes = [], M }) {

  drawables.forEach(d => prepareAndAdd(queue, d, styleData, clipplanes, M));
}

function createConstantLengthGrid({ points, upDir = mat.Z, upHeight, cellLength = 0.25, cellHeight = cellLength, camPos, cullOptions = CULL_NONE }) {

  // only draw if there is elevation
  if (upHeight === 0) {
    return [];
  }

  if (cullOptions === CULL_FRONT_AND_BACK) {
    return [];
  }

  const n = points.cols();

  // in case of line, do not close

  const zticks = [];
  let zOffset = 0;
  while (zOffset < upHeight) {
    zticks.push(zOffset);
    zOffset += cellHeight;
  }
  zticks.push(upHeight);

  const topOffset = mat.scale(upDir, upHeight);
  const result = [];
  // for lines, do not go back
  const maxSteps = n === 2 ? n - 1 : n;
  for (let i = 0; i < maxSteps; i++) {
    const a = mat.col(points, i);
    const b = mat.col(points, (i + 1) % n);
    const d = mat.add(a, topOffset);
    const sideLength = mat.norm(mat.sub(a, b));

    const ticks = [];
    let offset = 0;
    while (offset < sideLength) {
      ticks.push(offset);
      offset += cellLength;
    }
    ticks.push(sideLength);
    const lines = createSideGridFromTicks(a, b, d, ticks, zticks, camPos, cullOptions);
    result.push(...lines);
  }
  return result;
}

function createSideGridFromTicks(a, b, d, tticks, zticks, camPos, cullType) {

  const v0 = mat.fromTo(a, b);
  const v1 = mat.fromTo(a, d);

  const l0 = mat.norm(v0);
  const l1 = mat.norm(v1);

  if (l0 < 1E-7 || l1 < 1E-7) {
    // singular side
    return [];
  }
  const n = mat.cross(v0, v1);


  const lines = [];

  if (!doCull(a, n, camPos, cullType)) {
    for (let i = 0; i < tticks.length; i++) {
      const tx = tticks[i];
      const p0 = mat.add(a, mat.scale(v0, tx / l0));
      const p1 = mat.add(p0, v1);
      lines.push(LineStrip.new(MatF32.fromCols([p0, p1])));
    }

    for (let i = 0; i < zticks.length; i++) {
      const tx = zticks[i];
      const p0 = mat.add(a, mat.scale(v1, tx / l1));
      const p1 = mat.add(p0, v0);
      lines.push(LineStrip.new(MatF32.fromCols([p0, p1])));
    }
  }
  return lines;
}

function prepareAndAdd(queue, drawable, data, clipplanes, M, overwriteZ, id) {
  drawable = clipDrawable(drawable, clipplanes);
  if (drawable) {
    if (M) {
      drawable = drawable.transform(M);
    }
    const drawableDisplayable = Displayable.new(drawable, data,
      mat.optional(overwriteZ, drawable.center.at(2)), id);
    queue.push(drawableDisplayable);
  }
};

function makePerspectiveBoundDrawables(object, backgroundQueue, foregroundQueue, clipplanes, VP, camPos, data) {

  const bbLower = object.xyboundsWorld;

  const li = (i) => mat.col(bbLower, i);


  const z = object.options.z;

  if (object.options.showBoundGridBottom) {


    const polySideGrids = createConstantLengthGrid({
      points: MatF32.fromCols([li(0), li(1)]),
      upDir: mat.normalize(mat.cross(mat.Z,
        mat.fromTo(li(0), li(1)))), upHeight: mat.norm(mat.sub(li(3), li(0))),
      cellLength: object.options.gridCellLength,
      camPos: camPos,
      cullOptions: CULL_NONE
    });
    prepareAll({
      queue: backgroundQueue, drawables: polySideGrids,
      styleData: data,
      clipplanes: clipplanes, M: VP
    });
  }

  if (z && z > 0) {

    const bbUpper = MatF32.copy(bbLower);
    // translate
    mat.fill(mat.row(bbUpper, 2), z);

    const ui = (i) => mat.col(bbUpper, i);

    // put upper grid in background queue if needed

    if (object.options.showBoundGridTop) {
      // const upperGrid = createSideGrid(ui(0), ui(1), ui(2), ui(3), camPos, CULL_NONE);
      // upperGrid.forEach(g => prepareAndAdd(backgroundQueue, g, data, clipplanes, VP));

      const polySideGrids = createConstantLengthGrid({
        points: MatF32.fromCols([ui(0), ui(1)]),
        upDir: mat.normalize(mat.cross(mat.Z,
          mat.fromTo(ui(0), ui(1)))), upHeight: mat.norm(mat.sub(ui(3), ui(0))),
        cellLength: object.options.gridCellLength,
        camPos: camPos,
        cullOptions: CULL_NONE
      });

      prepareAll({
        queue: backgroundQueue, drawables: polySideGrids,
        styleData: data,
        clipplanes: clipplanes, M: VP
      });

    }
  }



}

function createVanishingLinePoints(queue, aWorld, bWorld, nearPlane, clipplanesWindow, VP, dataPoint, dataLine) {

  const dirWorld = mat.fromTo(aWorld, bWorld);
  // point behind near plane
  // project onto it
  if (!pointInside(aWorld, nearPlane)) {
    [aWorld,] = intersectLinePlane(aWorld, dirWorld, nearPlane);
  }
  if (!pointInside(bWorld, nearPlane)) {
    [bWorld,] = intersectLinePlane(bWorld, dirWorld, nearPlane);
  }
  const dirH = mat.mult(VP, mat.hvec(dirWorld, 0));
  const aH = mat.subvec(mat.homogenize(mat.mult(VP,
    mat.hvec(aWorld))), 0, 3);
  const bH = mat.subvec(mat.homogenize(mat.mult(VP,
    mat.hvec(bWorld))), 0, 3);

  if (Math.abs(dirH.at(3)) < 1E-7) {
    // TOOD handle points at infinity
    // Just draw parallel lines up to some length?

    return [];
  }
  const vp = MatF32.copy(mat.subvec(mat.homogenize(dirH), 0, 3));

  const isBBehind = mat.dot(mat.fromTo(aH, bH), mat.fromTo(aH, vp)) < 0;

  const vanishingPoint = Point.new(vp, 3);
  prepareAndAdd(queue, vanishingPoint, dataPoint, clipplanesWindow);
  const p0 = isBBehind ? bH : aH;
  let vanishingLine = LineStrip.new(MatF32.fromCols([p0, vp]));

  prepareAndAdd(queue, vanishingLine, dataLine, clipplanesWindow);

}


function createBoundVanishing(queue, object, nearPlane, clipplanesWindow, cam, VP, dataPoint, dataLine) {
  const bbLower = object.xyboundsWorld;

  const li = (i) => mat.col(bbLower, i);

  for (let i = 0; i < bbLower.cols(); i++) {
    const p0 = li(i);

    const p1 = li((i + 1) % bbLower.cols());

    createVanishingLinePoints(queue, p0, p1, nearPlane, clipplanesWindow, VP, dataPoint, dataLine);
  }

  const z = object.options.z;
  if (z && z > 0) {

    const bbUpper = MatF32.copy(bbLower);
    // translate
    mat.fill(mat.row(bbUpper, 2), z);

    const ui = (i) => mat.col(bbUpper, i);

    for (let i = 0; i < bbUpper.cols(); i++) {
      const p0 = ui(i);
      const p1 = ui((i + 1) % bbUpper.cols());

      createVanishingLinePoints(queue, p0, p1, nearPlane, clipplanesWindow, VP, dataPoint, dataLine);
    }
  }
}



function perspectiveRender(output, { objects, drawFloorGrid }, { viewport, cam },
  frustumPlanes, clipplanes, VP, linewidthScale = 0) {
  const backgroundQueue = [];
  const foregroundQueue = [];


  const boundgridData = Data.new(
    {
      strokeStyle: RGBA.new(0.2, 0.2, 0.2, 0.6),

    }
  );



  const W = mat.viewport(0, 0, output.getWidth(), output.getHeight());
  const WVP = mat.mult(W, VP);


  const clipplanesWindow = clipplanes.map(p => transformPlane(p, WVP));

  const { pos: camPos } = cam;

  // create text clipplanes for left/right
  let planeLeft = frustumPlanes[0];
  let planeRight = frustumPlanes[1];

  const textClipPlanes = [planeLeft, planeRight];

  // find left and right clip point (assuming horizon is not flipped on side)
  {
    const horizonPoints = [];
    // vanishing line
    const horizonPointsHom = [mat.mult(WVP, VecF32.from([1, 0, 0, 0])),
    mat.mult(WVP, VecF32.from([0, 1, 0, 0])),
    mat.mult(WVP, VecF32.from([1, 1, 0, 0]))];

    for (let i = 0; i < horizonPointsHom.length; i++) {
      const vph = horizonPointsHom[i];
      if (Math.abs(vph.at(3)) < 1E-7) {
        continue;
      }
      const vp = mat.subvec(mat.homogenize(vph), 0, 3);
      horizonPoints.push(vp);
    }

    if (horizonPoints.length > 1) {
      const vx = horizonPoints[0];
      const vz = horizonPoints[1];

      // in window coordinates
      const [sleft,] = intersectLinePlane(vx, vz, planeFromPointNormal(VecF32.from([0, 0, 0]), VecF32.from([1, 0, 0])));
      const [sright,] = intersectLinePlane(vx, vz, planeFromPointNormal(VecF32.from([output.getWidth(), 0, 0]), VecF32.from([1, 0, 0])));

      const horizonDrawable = LineStrip.new(
        MatF32.from([sleft.at(0), sleft.at(1), 0, sright.at(0), sright.at(1), 0], 3, 2)
      );
      const horizon = Displayable.new(horizonDrawable, Data.new({
        strokeStyle: RGBA.BLACK
      }), horizonDrawable.center.at(2));

      backgroundQueue.push(horizon);

    }

  }


  const floorGridData = Data.new({
    strokeStyle: RGBA.AQUA.cp().a(0.45)
  });

  const { center: fc, halfSize: fh } = viewport;

  const floorgrid = [];

  if (drawFloorGrid) {
    const bmin = mat.sub(fc, fh);
    let bx = bmin.at(0);
    let by = bmin.at(1);
    const cellSize = zoom_scale(viewport.zoomLevel);
    bx = cellSize * Math.floor(bx / cellSize);
    by = cellSize * Math.floor(by / cellSize);

    const dx = 2.0 * fh.at(0);
    const dy = 2.0 * fh.at(1);

    const numX = Math.ceil(dx / cellSize) + 1;
    const numY = Math.ceil(dy / cellSize) + 1;
    const vx = mat.scale(mat.X, cellSize * numX);
    const vy = mat.scale(mat.Y, cellSize * numY);

    for (let i = 0; i <= numX; i++) {
      const p0 = VecF32.from([bx + i * cellSize, by, 0]);
      const p1 = mat.add(p0, vy);
      floorgrid.push(LineStrip.new(MatF32.fromCols([p0, p1])));
    }

    for (let i = 0; i <= numY; i++) {
      const p0 = VecF32.from([bx, by + i * cellSize, 0]);
      const p1 = mat.add(p0, vx);
      floorgrid.push(LineStrip.new(MatF32.fromCols([p0, p1])));
    }
  }



  for (let i = 0; i < floorgrid.length; i++) {
    prepareAndAdd(backgroundQueue, floorgrid[i], floorGridData, clipplanes, WVP);
  }



  if (objects) {

    const vanishingLineData = Data.new({
      strokeStyle: RGBA.GRAY.cp().a(0.4)
    });


    for (let i = 0; i < objects.length; i++) {

      const oi = objects[i];

      const drawableWorld = oi.drawableWorld;

      makePerspectiveBoundDrawables(oi, backgroundQueue, foregroundQueue, clipplanes, WVP, camPos, boundgridData);

      let gpoints = mat.copy(oi.drawableWorld.points);
      const zStart = oi.options.sideGridStart;
      let zHeight = oi.options.z;
      if (zStart !== 0) {
        mat.fill(mat.row(gpoints, 2), zStart);
        zHeight = Math.max(0, zHeight - zStart);
      }
      const polySideGrids = createConstantLengthGrid({
        points: gpoints,
        upDir: mat.Z, upHeight: zHeight,
        cellLength: oi.options.gridCellLength,
        cellHeight: oi.options.gridCellHeight,
        camPos: camPos,
        cullOptions: options2CullTypeSide(oi.options)
      });
      prepareAll({
        queue: foregroundQueue, drawables: polySideGrids,
        styleData: oi.styleData,
        clipplanes: clipplanes, M: WVP
      });

      {
        // draw object itself
        let dObj = oi.drawableWorld;
        prepareAndAdd(foregroundQueue, dObj, oi.styleData, clipplanes, WVP);
      }

      // vanishing lines
      if (oi.options.showBoundVanishingLines) {
        createBoundVanishing(backgroundQueue, oi, frustumPlanes[4], clipplanesWindow, cam, WVP,
          Data.new(), vanishingLineData);
      }

      const z = oi.options.z;
      if (z && z > 0) {
        const TZ = mat.translation(VecF32.from([0, 0, z]));
        const drawTrans = drawableWorld.transform(TZ);

        prepareAndAdd(foregroundQueue, drawTrans, oi.styleData, clipplanes, WVP);

        // draw lines between vertices
        const pw = drawableWorld.points;
        const pt = drawTrans.points;
        const n = pw.cols();
        const lineData = oi.styleData.copy();

        lineData.set("lineWidth", lineData.get("lineWidth", 1) * 2);
        for (let j = 0; j < n; j++) {
          const line = LineStrip.new(MatF32.fromCols([mat.col(pw, j), mat.col(pt, j)]));

          prepareAndAdd(foregroundQueue, line, lineData, clipplanes, WVP);
        }

        // set length markers

        if (oi.options.displayCellLength) {

          const culltype = options2CullTypeSide(oi.options);
          // find closest poly point
          let noCullLast = false;
          for (let j = 0; j < n; j++) {
            const pj = mat.col(pw, j);
            const pjn = mat.col(pw, (j + 1) % n);
            const normal = mat.cross(mat.fromTo(pj, pjn), mat.Z);
            if (doCull(pj, normal, camPos, culltype)) {
              if (!noCullLast) {
                noCullLast = false;
                continue;

              }
              noCullLast = false;

            } else {
              noCullLast = true;
            }
            let clipped = false;

            for (let k = 0; k < textClipPlanes.length; k++) {
              if (!pointInside(pj, textClipPlanes[k])) {
                clipped = true;
                break;
              }
            }

            if (clipped) {
              const pjz = mat.copy(pj);
              pjz.set(z, 2);
              clipped = false;
              for (let k = 0; k < textClipPlanes.length; k++) {
                if (!pointInside(pjz, textClipPlanes[k])) {
                  clipped = true;
                  break;
                }
              }
            }
            if (clipped) {
              continue;
            }
            const { gridCellHeight } = oi.options;
            const textStart = mat.copy(pj);
            const zStart = oi.options.sideGridStart;
            textStart.set(zStart, 2);

            const numZ = Math.ceil((z - zStart) / gridCellHeight);
            for (let j = 0; j <= numZ; j++) {
              const zOffset = j * gridCellHeight;
              const point = mat.add(textStart, mat.scale(mat.Z, zOffset));
              prepareAndAdd(foregroundQueue, Text.new(point, formatNumber(zOffset, 3)),
                oi.styleData, [frustumPlanes[4]], WVP);
            }

          }
        }
      }
    }
  }

  backgroundQueue.sort((a, b) => b.z - a.z);
  foregroundQueue.sort((a, b) => b.z - a.z);

  const isScaleLineWidth = linewidthScale !== 0;
  const scale = isScaleLineWidth ? Math.max(1, Math.round(linewidthScale / 2)) : 1;
  for (let i = 0; i < backgroundQueue.length; i++) {
    const di = backgroundQueue[i];
    let style = di.styleData;
    if (isScaleLineWidth) {
      style = style.copy();
      style.set("lineWidth", style.get("lineWidth", 1) * scale);
    }
    di.drawable.draw(output, style);
  }

  for (let i = 0; i < foregroundQueue.length; i++) {
    const di = foregroundQueue[i];
    let style = di.styleData;
    if (isScaleLineWidth) {
      style = style.copy();
      style.set("lineWidth", style.get("lineWidth", 1) * scale);
    }
    di.drawable.draw(output, style);
  }

}

function PerspectiveView(props) {
  const context = useContext(ModelContext);
  const { objects } = props;
  const { viewport, cam } = context;
  const [canvas, canvasRef] = useComponentRef();
  const [imageSizeMultiplier, setImageSizeMultiplier] = useState(2);

  const [camSpeedLevel, setCamSpeedLevel] = useState(viewport.zoomLevel);
  const [drawFloorGrid, setDrawFloorGrid] = useState(true);
  const camSpeed = zoom_scale(camSpeedLevel);

  const V = props.V;
  const P = props.P;
  const VP = mat.mult(P, V);


  let clipplanes = [];
  const frustumPlanes = computeFrustumPlanes(VP);
  // clipplanes.push(frustumPlanes[4]);
  clipplanes.push(...computeFrustumPlanes(VP));
  clipplanes.splice(5, 1);
  // near clip in window coordinates
  // clipplanes.push({ n: VecF32.from([1, 0, 0]), p: VecF32.from([160, 0, 0]) });


  useFirstPersonController({
    cam: context.cam, setCam: context.setCam,
    camUp: mat.Z, canvas, speed: camSpeed
  });

  const { setPerspective } = context;
  useEffect(() => {
    if (canvas === null) {
      return;
    }
    setPerspective(cur => ({
      fov: cur.fov,
      aspect: (canvas.width / canvas.height)
    }));
  }, [canvas, setPerspective]);


  useEffect(() => {
    if (canvas === null) {
      return;
    }
    const output = CanvasOutput.new(Screen.new().canvas(canvas));

    output.initFrame();

    perspectiveRender(output, { objects, drawFloorGrid }, { viewport, cam }, frustumPlanes, clipplanes, VP);

    output.finishFrame();
  }, [VP, frustumPlanes, clipplanes, canvas, objects, viewport, cam, drawFloorGrid]);

  const downloadSVGButton = () => {
    if (canvas === null) {
      return;
    }
    const output = SVGOutput.new(canvas.width, canvas.height);

    output.initFrame();
    perspectiveRender(output, { objects, drawFloorGrid }, context, frustumPlanes, clipplanes, VP);

    output.finishFrame();

    const currentTime = new Date();
    const formattedTime = currentTime.getFullYear() + "-" +
      (currentTime.getMonth() + 1) + "-" +
      currentTime.getDate()

    output.download(formattedTime + "_perspective_drawing");
  };

  const downloadHighResPNGButton = () => {
    if (canvas === null) {
      return;
    }

    const canvasNew = document.createElement("canvas");
    canvasNew.width = imageSizeMultiplier * canvas.width;
    canvasNew.height = imageSizeMultiplier * canvas.height;
    const output = CanvasOutput.new(Screen.new().canvas(canvasNew));

    output.initFrame();
    perspectiveRender(output, { objects, drawFloorGrid },
      context, frustumPlanes, clipplanes, VP,
      imageSizeMultiplier);

    output.finishFrame();

    const currentTime = new Date();
    const formattedTime = currentTime.getFullYear() + "-" +
      (currentTime.getMonth() + 1) + "-" +
      currentTime.getDate()
    output.download(formattedTime + "_perspective_drawing");
  };
  return <div>
    <canvas tabIndex="-1" ref={canvasRef}
      width={props.width} height={props.height}
      className="perspectiveCanvas">
      Canvas not supported
    </canvas>
    <div className="flex-container">
      <div className="flex-entry">
        <span className="title">
          3D Settings
      </span>
        <ValueSlider value={camSpeedLevel} handleChange={v => setCamSpeedLevel(v)}
          valueName="Movement speed" min={0} max={50} step={1} precision={0}
        />
        <CheckBox checked={drawFloorGrid} labelText="Draw floor grid"
          handleChange={v => setDrawFloorGrid(v)} />
      </div>

      <div className="flex-entry">
        <span className="title">
          Options
        </span>
        <CanvasResizer
          canvas={canvas}
          buttonName="Resize 3D canvas"
          position="right"
          handleSizeChange={
            (w, h) => {
              setPerspective(cur => ({
                fov: cur.fov,
                aspect: w / h
              }));
            }
          }
        />

      </div>

      <div className="flex-entry">
        <span className="title">
          Download
          </span>
        <ValueSlider value={imageSizeMultiplier}
          handleChange={v => setImageSizeMultiplier(v)}
          valueName="PNG size multiplier" min={1} max={5} step={1} precision={0}
        />
        <button onClick={downloadSVGButton}>Download SVG</button>
        <button onClick={downloadHighResPNGButton}>Download PNG</button>
      </div>
    </div>
  </div>
}

function CanvasResizer({ canvas, buttonName = "Resize Canvas",
  handleSizeChange, position = "left" }) {


  const [show, setShow] = useState(false);
  const [curWidth, setCurWidth] = useState(0);
  const [curHeight, setCurHeight] = useState(0);

  const width = canvas ? canvas.width : 0;
  const height = canvas ? canvas.height : 0;

  useEffect(() => {
    setCurWidth(w => width);
    setCurHeight(h => height);
  }, [width, height]);


  if (!canvas) {
    return (
      <div>
        <button disabled={true}>{buttonName}</button>
      </div >
    );
  }

  let className = "resize-canvas-window";
  if (position === "left") {
    className += " left-to";
  }
  else if (position === "right") {
    className += " right-to";
  }
  const window = (
    <div className="popover">
      <div className="fullscreen transparent" onClick={
        () => setShow(() => false)
      } />
      <div className={className}>
        <span className="title">
          Resize canvas
        </span>
        <div>
          Width: <NumberInput
            value={curWidth} size={4}
            handleChange={e => setCurWidth(e)} />
        </div>
        <div>
          Height: <NumberInput
            value={curHeight} size={4}
            handleChange={e => setCurHeight(e)}
          />
        </div>
        <div>
          <button onClick={() => {
            canvas.width = Math.max(1, curWidth);
            canvas.height = Math.max(1, curHeight);
            handleSizeChange(curWidth, curHeight);
            setShow(false);
          }}>Update size</button>
          <button onClick={() => setShow(false)}>Cancel</button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <button onClick={() => setShow(true)}>{buttonName}</button>
      {show ? window : null}
    </div>
  );

}


const ModelContext = React.createContext();


function ObjectHandler(props) {
  const [objects, setObjects] = useState([]);

  const [cam, setCam] = useState({
    pos: VecF32.from([-0.4, -1.5, 1]),
    dir: VecF32.from([0.4, 1.5, -1]),
    up: mat.Z
  });
  const [perspective, setPerspective] = useState({
    fov: mat.deg2rad(90),
    aspect: 1
  });

  const [viewport, setViewport] = useState({
    center: VecF32.from([0, 0, 0]),
    halfSize: VecF32.from([computeViewportHalfSize(0), computeViewportHalfSize(0), 0]),
    zoomLevel: 0
  });

  const P = mat.perspective(perspective.fov, perspective.aspect, 0.1, 100);
  const V = mat.lookAt(cam.pos, mat.add(cam.pos, cam.dir), mat.Z);

  let changeObject = useCallback((object, index) => {
    setObjects(objects => {
      const objectsNew = [...objects];
      objectsNew[index] = object;
      return objectsNew;
    });
  }, []);

  let onAddObject = useCallback((object) => {
    setObjects(objects => objects.concat(object))
  }, []);

  let onDeleteObject = useCallback((object, index) => {
    setObjects(objects => {
      const newObjects = [...objects];
      newObjects.splice(index, 1);
      return newObjects;
    });
  }, []);


  return <div>
    <ModelContext.Provider value={
      {
        objects, setObjects,
        cam, setCam,
        perspective, setPerspective,
        V,
        P,
        viewport, setViewport
      }
    }>
      <div className="main-container">
        <div className="perspective-view">
          <PerspectiveView width="720" height="405" objects={objects} V={V} P={P} viewport={viewport}></PerspectiveView>
        </div>
        <div className="edit-view">
          <EditCanvas width="400" height="400"
            objects={objects} objChangeHandler={changeObject}
            onAddObject={onAddObject} handleDelete={onDeleteObject} />
        </div>
      </div>
    </ModelContext.Provider>
  </div>;
}

function App() {
  return (
    <div className="App">
      {/* <header className="App-header">
        {<img src={logo} className="App-logo" alt="logo" />/}
      </header> */}
      <ObjectHandler></ObjectHandler>
    </div>
  );
}

export default App;
